import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Tabs,
  Modal,
  Descriptions,
  Form,
  Select,
  InputNumber,
  DatePicker,
  message,
  Popconfirm,
  theme,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BankOutlined,
  TeamOutlined,
  DesktopOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import api from "@/libs/api";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import VgShareManagement from "@components/settlements/VgShareManagement.react";
import dayjs from "dayjs";

const { Title, Text } = Typography;

function formatPower(val, unit) {
  if (val >= 1000) {
    return { value: (val / 1000).toFixed(1), suffix: unit === "kW" ? "MW" : "MWh" };
  }
  return { value: val.toFixed(2), suffix: unit };
}

export default function CompanyList() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("active");
  const [searchText, setSearchText] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [summary, setSummary] = useState({ totalViewGroups: 0, totalSystems: 0 });
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);

  // Add Company modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [addLoading, setAddLoading] = useState(false);

  // Revenue Sharing modal (VG-based, np_view_group_company_share)
  const [revenueModalOpen, setRevenueModalOpen] = useState(false);
  const [revenueForm] = Form.useForm();
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [availableVgs, setAvailableVgs] = useState([]);
  const [revenueCompany, setRevenueCompany] = useState(null);
  const [previewSum, setPreviewSum] = useState(null);

  // Permission gate — only superadmin / users with settlement:run can edit shares
  const roleType = (typeof localStorage !== "undefined" ? localStorage.getItem("roleType") : "") || "";
  const isSuperadmin = roleType === "SUPERADMIN";
  const userPermissions = (() => {
    try { return JSON.parse(localStorage.getItem("permissions") || "[]"); }
    catch { return []; }
  })();
  const canEditShares = isSuperadmin || userPermissions.includes("settlement:run");

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/companies");
      if (res.data.success && res.data.data) {
        setCompanies(res.data.data);
        if (res.data.summary) setSummary(res.data.summary);
      }
    } catch (err) {
      console.error("Failed to fetch companies:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompany = async () => {
    try {
      const values = await addForm.validateFields();
      setAddLoading(true);
      const res = await api.post("/api/companies", values);
      if (res.data.success) {
        message.success(`Company "${values.name}" created (pending approval)`);
        setAddModalOpen(false);
        addForm.resetFields();
        fetchCompanies();
      } else {
        message.error(res.data.message || "Failed to create company");
      }
    } catch (err) {
      if (err.errorFields) return; // validation error
      console.error("Failed to create company:", err);
      message.error("Failed to create company");
    } finally {
      setAddLoading(false);
    }
  };

  const handleApprove = async (companyId) => {
    try {
      const res = await api.post(`/api/companies/${companyId}/approve`);
      if (res.data.success) {
        message.success("Company approved");
        fetchCompanies();
      } else {
        message.error(res.data.message || "Failed to approve");
      }
    } catch (err) {
      console.error("Failed to approve company:", err);
      message.error("Failed to approve company");
    }
  };

  const handleReject = async (companyId) => {
    try {
      const res = await api.post(`/api/companies/${companyId}/reject`);
      if (res.data.success) {
        message.success("Company rejected");
        fetchCompanies();
      } else {
        message.error(res.data.message || "Failed to reject");
      }
    } catch (err) {
      console.error("Failed to reject company:", err);
      message.error("Failed to reject company");
    }
  };

  const openRevenueModal = async (company) => {
    setRevenueCompany(company);
    setPreviewSum(null);
    revenueForm.resetFields();
    revenueForm.setFieldsValue({
      effectiveFrom: dayjs(),
    });

    // Load all view groups for the picker (uses the same endpoint other UIs use)
    try {
      const userId = localStorage.getItem("userId");
      const res = await api.get(`/api/groups?userId=${userId}`);
      if (res.data?.success && Array.isArray(res.data.data)) {
        setAvailableVgs(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch view groups:", err);
    }
    setRevenueModalOpen(true);
  };

  // Probe current VG sum so the user sees "remaining %" before submitting.
  const refreshPreviewSum = async (viewGroupId, effectiveFrom) => {
    if (!viewGroupId || !effectiveFrom) { setPreviewSum(null); return; }
    try {
      const fromStr = dayjs.isDayjs(effectiveFrom) ? effectiveFrom.format("YYYY-MM-DD") : effectiveFrom;
      const res = await api.get(`/api/vg-shares/preview-sum?view_group_id=${viewGroupId}&effective_from=${fromStr}`);
      if (res.data?.success) {
        const existing = Number(res.data.data.existingSum);
        setPreviewSum({ existingSum: existing, remaining: 100 - existing });
      }
    } catch (e) {
      setPreviewSum(null);
    }
  };

  const handleSetRevenue = async () => {
    try {
      const values = await revenueForm.validateFields();
      setRevenueLoading(true);

      const body = {
        view_group_id: values.viewGroupId,
        company_id: revenueCompany.id,
        share_percent: Number(values.revenueSharePct),
        description: values.description || null,
        effective_from: values.effectiveFrom
          ? values.effectiveFrom.format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        effective_to: values.effectiveTo ? values.effectiveTo.format("YYYY-MM-DD") : null,
        priority: 100,
        is_active: 1,
      };

      const res = await api.post(`/api/vg-shares`, body);
      if (res.data?.success) {
        message.success(`Revenue share set: ${revenueCompany.name} ← ${values.revenueSharePct}% of ${values.viewGroupId}`);
        setRevenueModalOpen(false);
      } else {
        message.error(res.data?.message || "Failed to set revenue share");
      }
    } catch (err) {
      if (err.errorFields) return;
      // 409 from sum-validation surfaces the detailed message
      const apiMsg = err.response?.data?.message;
      console.error("Failed to set revenue share:", err);
      message.error(apiMsg || "Failed to set revenue share");
    } finally {
      setRevenueLoading(false);
    }
  };

  const filteredCompanies = companies.filter((company) => {
    const matchesTab = activeTab === "active" ? company.status === "active" : company.status === "pending";
    const matchesSearch =
      !searchText ||
      company.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      (company.contactName || "").toLowerCase().includes(searchText.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const stats = {
    active: companies.filter((c) => c.status === "active").length,
    pending: companies.filter((c) => c.status === "pending").length,
    totalGroups: summary.totalViewGroups,
    totalSystems: summary.totalSystems,
  };

  const columns = [
    {
      title: "Company",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Button
            type="link"
            onClick={() => navigate(`/company/snapshot/${record.id}`)}
            style={{ padding: 0, height: "auto" }}
          >
            {name}
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.id}</Text>
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <Tag color={type === "Platform" ? "blue" : "green"}>{type}</Tag>
      ),
    },
    {
      title: "Groups",
      dataIndex: "groups",
      key: "groups",
      align: "center",
    },
    {
      title: "Systems",
      dataIndex: "systems",
      key: "systems",
      align: "center",
    },
    {
      title: "Capacity",
      key: "capacity",
      align: "right",
      render: (_, record) => {
        const fKw = formatPower(record.totalKw || 0, "kW");
        const fKwh = formatPower(record.totalKwh || 0, "kWh");
        return (
        <Space direction="vertical" size={0} style={{ textAlign: "right" }}>
          <Text>{fKw.value} {fKw.suffix}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{fKwh.value} {fKwh.suffix}</Text>
        </Space>
        );
      },
    },
    {
      title: "Online Rate",
      dataIndex: "onlineRate",
      key: "onlineRate",
      align: "center",
      render: (rate) =>
        rate > 0 ? (
          <Tag color={rate >= 95 ? "success" : rate >= 90 ? "warning" : "error"}>
            {rate.toFixed(1)}%
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Contact",
      key: "contact",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.contactName || "—"}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.contactEmail || "—"}</Text>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setSelectedCompany(record);
              setDetailModalOpen(true);
            }}
          >
            View
          </Button>
          <Button type="link" size="small" onClick={() => navigate(`/company/snapshot/${record.id}`)}>
            Snapshot
          </Button>
          {/* Per-company "Revenue" button removed — partner revenue shares
              are managed globally at /settlements/vg-shares (Super Admin). */}
        </Space>
      ),
    },
  ];

  const pendingColumns = [
    columns[0], // Company
    columns[1], // Type
    {
      title: "ABN",
      dataIndex: "abn",
      key: "abn",
      render: (abn) => abn || "—",
    },
    {
      title: "Submitted",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (d) => d ? dayjs(d).format("YYYY-MM-DD") : "—",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setSelectedCompany(record);
              setDetailModalOpen(true);
            }}
          >
            View
          </Button>
          <Popconfirm
            title="Approve this company?"
            onConfirm={() => handleApprove(record.id)}
            okText="Approve"
          >
            <Button type="link" size="small" style={{ color: token.colorSuccess }}>
              Approve
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Reject this company?"
            onConfirm={() => handleReject(record.id)}
            okText="Reject"
            okType="danger"
          >
            <Button type="link" size="small" danger>
              Reject
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: "active",
      label: (
        <Space>
          <CheckCircleOutlined />
          Active Companies
          <Tag color="success">{stats.active}</Tag>
        </Space>
      ),
    },
    {
      key: "pending",
      label: (
        <Space>
          <ClockCircleOutlined />
          Pending Approval
          <Tag color="warning">{stats.pending}</Tag>
        </Space>
      ),
    },
    // Partner Revenue Shares — Super Admin only.
    // Centralized CRUD for np_view_group_company_share. Per-company
    // CompanySnapshot still shows a read-only view of these rows.
    ...(isSuperadmin ? [{
      key: "vg-shares",
      label: (
        <Space>
          <PercentageOutlined />
          Partner Revenue Shares
        </Space>
      ),
    }] : []),
  ];

  return (
    <PageLayout active="companyList">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              Company List
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Manage partner companies and pending approvals
            </Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => setAddModalOpen(true)}>
            Add Company
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Active Companies"
              value={stats.active}
              prefix={<BankOutlined />}
              valueStyle={{ color: token.colorSuccess }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Pending Approval"
              value={stats.pending}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: token.colorWarning }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Groups"
              value={stats.totalGroups}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Systems"
              value={stats.totalSystems}
              prefix={<DesktopOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Company Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ padding: "16px 24px", borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              style={{ marginBottom: -17 }}
            />
            {activeTab !== "vg-shares" && (
              <Input
                placeholder="Search companies..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 250 }}
                allowClear
              />
            )}
          </div>
        </div>
        {activeTab === "vg-shares" ? (
          <VgShareManagement />
        ) : (
          <Table
            columns={activeTab === "active" ? columns : pendingColumns}
            dataSource={filteredCompanies}
            rowKey="id"
            loading={loading}
            pagination={makePagination(pageSize, setPageSize)}
            style={{ padding: "0 24px 24px" }}
          />
        )}
      </Card>

      {/* Detail Modal */}
      <Modal
        title={selectedCompany?.name}
        open={detailModalOpen}
        onCancel={() => setDetailModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalOpen(false)}>
            Close
          </Button>,
          <Button key="snapshot" type="primary" onClick={() => navigate(`/company/snapshot/${selectedCompany?.id}`)}>
            View Snapshot
          </Button>,
        ]}
        width={600}
      >
        {selectedCompany && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Company ID">{selectedCompany.id}</Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag color={selectedCompany.type === "Platform" ? "blue" : "green"}>
                {selectedCompany.type}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={selectedCompany.status === "active" ? "success" : "warning"}>
                {selectedCompany.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              {selectedCompany.createdAt ? dayjs(selectedCompany.createdAt).format("YYYY-MM-DD") : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Contact">{selectedCompany.contactName || "—"}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedCompany.contactEmail || "—"}</Descriptions.Item>
            <Descriptions.Item label="Groups">{selectedCompany.groups}</Descriptions.Item>
            <Descriptions.Item label="Systems">{selectedCompany.systems}</Descriptions.Item>
            <Descriptions.Item label="Inverter Capacity">{(() => { const f = formatPower(selectedCompany.totalKw || 0, "kW"); return `${f.value} ${f.suffix}`; })()}</Descriptions.Item>
            <Descriptions.Item label="Battery Capacity">{(() => { const f = formatPower(selectedCompany.totalKwh || 0, "kWh"); return `${f.value} ${f.suffix}`; })()}</Descriptions.Item>
            <Descriptions.Item label="Online Rate">
              {selectedCompany.onlineRate > 0 ? `${selectedCompany.onlineRate.toFixed(1)}%` : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="ABN">{selectedCompany.abn || "—"}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Add Company Modal */}
      <Modal
        title="Add New Company"
        open={addModalOpen}
        onCancel={() => { setAddModalOpen(false); addForm.resetFields(); }}
        onOk={handleAddCompany}
        confirmLoading={addLoading}
        okText="Create Company"
        width={520}
      >
        <Form form={addForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Company Name" rules={[{ required: true, message: "Company name is required" }]}>
            <Input placeholder="e.g. Solar Partners Pty Ltd" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="abn" label="ABN">
                <Input placeholder="e.g. 12 345 678 901" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="emailDomain" label="Email Domain">
                <Input placeholder="e.g. solarpartners.com.au" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="address" label="Address">
            <Input placeholder="Street address" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="suburb" label="Suburb">
                <Input placeholder="e.g. Sydney" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="postcode" label="Postcode">
                <Input placeholder="e.g. 2000" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="website" label="Website">
            <Input placeholder="e.g. https://solarpartners.com.au" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Revenue Share Setup Modal — VG-based (np_view_group_company_share) */}
      <Modal
        title={`Add Revenue Share — ${revenueCompany?.name || ""}`}
        open={revenueModalOpen}
        onCancel={() => setRevenueModalOpen(false)}
        onOk={handleSetRevenue}
        confirmLoading={revenueLoading}
        okText="Save"
        width={560}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Pick the view group that pays this company a share. For multiple shares
          (e.g. own council 12% + founding council 10%), repeat with different VGs.
        </Text>
        <Form form={revenueForm} layout="vertical">
          <Form.Item
            name="viewGroupId"
            label="View Group"
            rules={[{ required: true, message: "Pick a view group" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select view group"
              options={availableVgs.map((vg) => ({
                value: vg.id,
                label: `${vg.name} (${vg.id})`,
              }))}
              onChange={(vgId) => {
                const ef = revenueForm.getFieldValue("effectiveFrom");
                refreshPreviewSum(vgId, ef);
              }}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="revenueSharePct"
                label="Revenue Share %"
                rules={[{ required: true, message: "Enter share %" }]}
              >
                <InputNumber
                  min={0}
                  max={100}
                  step={0.5}
                  addonAfter="%"
                  placeholder="e.g. 12"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="effectiveFrom"
                label="Effective From"
                rules={[{ required: true, message: "Required" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  onChange={(date) => {
                    const vgId = revenueForm.getFieldValue("viewGroupId");
                    refreshPreviewSum(vgId, date);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          {previewSum && (
            <div style={{ marginBottom: 16, padding: "8px 12px", background: token.colorFillTertiary, borderRadius: 6 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Current allocations in this VG (overlapping window):
                {" "}<Text strong>{previewSum.existingSum.toFixed(2)}%</Text>{" — "}
                Remaining: <Text strong style={{ color: previewSum.remaining > 0 ? token.colorSuccess : token.colorError }}>
                  {previewSum.remaining.toFixed(2)}%
                </Text>
              </Text>
            </div>
          )}

          <Form.Item name="effectiveTo" label="Effective To (leave empty for open-ended)">
            <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item name="description" label="Description (optional)">
            <Input.TextArea
              rows={2}
              placeholder="e.g. clause 2.2c founding council share"
            />
          </Form.Item>
        </Form>
      </Modal>
    </PageLayout>
  );
}
